import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MarketingLayout, Section, Overline } from "@/marketing/MarketingLayout";
import { CONTACT } from "@/marketing/content";
import { Mail, Phone, User, MapPin, Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Email: Mail, Phone: Phone };

export default function MarketingContact() {
  const [form, setForm] = useState({ name: "", email: "", topic: CONTACT.topics[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll reply within 24 hours.");
      setForm({ name: "", email: "", topic: CONTACT.topics[0], message: "" });
    } catch {
      toast.error("Could not send. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout>
      <Section className="pt-16 pb-4">
        <Overline>{CONTACT.hero.eyebrow}</Overline>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-5 max-w-3xl">{CONTACT.hero.headline}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl">{CONTACT.hero.subheadline}</p>
      </Section>

      <Section className="pt-10 pb-4 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {CONTACT.details.map((d) => {
            const Icon = ICONS[d.label] || User;
            return (
              <div key={d.label} className="p-5 rounded-md border border-border bg-card" data-testid="contact-detail">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{d.label}</span>
                </div>
                <div className="font-medium">{d.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{d.note}</div>
              </div>
            );
          })}
          <div className="p-5 rounded-md border border-border bg-card">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Office</span>
            </div>
            <div className="font-medium">{CONTACT.office.address}</div>
            <div className="text-sm text-muted-foreground">{CONTACT.office.city}, {CONTACT.office.country}</div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="p-6 lg:p-8 rounded-md border border-border bg-card">
            {sent ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-success" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">Message sent!</h3>
                <p className="text-muted-foreground text-sm">Thanks for reaching out — our team will get back to you within one business day.</p>
                <Button variant="outline" className="rounded-md mt-6" onClick={() => setSent(false)} data-testid="contact-send-another">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <h3 className="font-heading font-bold text-xl">Send Us a Message</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="c-name">Name</Label>
                    <Input id="c-name" className="mt-1.5 rounded-md" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                  </div>
                  <div>
                    <Label htmlFor="c-email">Email</Label>
                    <Input id="c-email" type="email" className="mt-1.5 rounded-md" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <Label>Topic</Label>
                  <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                    <SelectTrigger className="mt-1.5 rounded-md" data-testid="contact-topic"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTACT.topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="c-msg">Message</Label>
                  <Textarea id="c-msg" className="mt-1.5 rounded-md h-32" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={loading} className="rounded-md gap-2 h-11" data-testid="contact-submit">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
